import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';

//const SUBS_URL = 'http://localhost:5002/api';
const SUBS_URL = process.env.REACT_APP_SUBS_URL || 'http://localhost:5002/api';


// ✅ Shared: save token + hard reload
const saveAndRedirect = (token: string, user: any) => {
    localStorage.setItem('sub_token', token);
    localStorage.setItem('sub_user', JSON.stringify(user));
    window.location.href = '/';
};

// ─── Countdown Timer ─────────────────────────────────────────────────────
const Countdown = ({ seconds, onExpire }: { seconds: number; onExpire: () => void }) => {
    const [left, setLeft] = useState(seconds);
    useEffect(() => {
        setLeft(seconds);
        const t = setInterval(() => setLeft(s => {
            if (s <= 1) { clearInterval(t); onExpire(); return 0; }
            return s - 1;
        }), 1000);
        return () => clearInterval(t);
    }, [seconds]);
    const m = Math.floor(left / 60);
    const s = left % 60;
    return <span className={`font-bold font-mono ${left <= 10 ? 'text-red-500' : 'text-teal-600'}`}>{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}</span>;
};

// ─── OTP Input ────────────────────────────────────────────────────────────
const OtpInput = ({ otp, setOtp, onEnter, error }: { otp: string[]; setOtp: (o: string[]) => void; onEnter: () => void; error: string }) => {
    const refs = useRef<(HTMLInputElement | null)[]>([]);
    useEffect(() => { refs.current[0]?.focus(); }, []);
    const handleChange = (i: number, val: string) => {
        if (!/^\d*$/.test(val)) return;
        const next = [...otp]; next[i] = val.slice(-1); setOtp(next);
        if (val && i < 5) refs.current[i + 1]?.focus();
    };
    const handleKeyDown = (i: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[i] && i > 0) refs.current[i - 1]?.focus();
        if (e.key === 'Enter') onEnter();
    };
    const handlePaste = (e: React.ClipboardEvent) => {
        const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (p.length === 6) { setOtp(p.split('')); refs.current[5]?.focus(); }
    };
    return (
        <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {otp.map((d, i) => (
                <input key={i} ref={el => { refs.current[i] = el; }}
                    type="text" inputMode="numeric" maxLength={1} value={d}
                    onChange={e => handleChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    className={`w-11 h-12 text-center text-lg font-bold border-2 rounded-xl focus:outline-none transition-all ${d ? 'border-teal-500 bg-teal-50 text-teal-700' : error ? 'border-red-300' : 'border-gray-200 focus:border-teal-400'}`}
                />
            ))}
        </div>
    );
};

// ─── Forgot Password Modal ────────────────────────────────────────────────
type ForgotStep = 'email' | 'otp' | 'reset';

const ForgotPasswordModal = ({ onClose }: { onClose: () => void }) => {
    const [step, setStep] = useState<ForgotStep>('email');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [newPass, setNewPass] = useState('');
    const [confirmPass, setConfirmPass] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [expired, setExpired] = useState(false);
    const [timerKey, setTimerKey] = useState(0);

    const handleSendOtp = async () => {
        if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) { setError('Enter a valid email'); return; }
        setLoading(true); setError('');
        try {
            const res = await fetch(`${SUBS_URL}/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim() }) });
            const data = await res.json();
            if (data.success) { setStep('otp'); setExpired(false); setTimerKey(k => k + 1); }
            else setError(data.message || 'Failed to send OTP');
        } catch { setError('Server error. Try again.'); }
        finally { setLoading(false); }
    };

    const handleVerifyOtp = async () => {
        const code = otp.join('');
        if (code.length !== 6) { setError('Enter the 6-digit OTP'); return; }
        setLoading(true); setError('');
        try {
            const res = await fetch(`${SUBS_URL}/auth/verify-forgot-otp`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, otpCode: code }) });
            const data = await res.json();
            if (data.success) { setStep('reset'); }
            else setError(data.message || 'Invalid OTP');
        } catch { setError('Server error. Try again.'); }
        finally { setLoading(false); }
    };

    const handleResetPassword = async () => {
        if (!newPass || newPass.length < 6) { setError('Minimum 6 characters'); return; }
        if (newPass !== confirmPass) { setError('Passwords do not match'); return; }
        setLoading(true); setError('');
        try {
            const res = await fetch(`${SUBS_URL}/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, otpCode: otp.join(''), newPassword: newPass }) });
            const data = await res.json();
            if (data.success) { toast.success('Password reset! Please login.'); onClose(); }
            else setError(data.message || 'Failed to reset');
        } catch { setError('Server error.'); }
        finally { setLoading(false); }
    };

    const handleResend = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${SUBS_URL}/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
            const data = await res.json();
            if (data.success) { setOtp(['', '', '', '', '', '']); setExpired(false); setTimerKey(k => k + 1); toast.success('New OTP sent!'); }
            else setError(data.message);
        } catch { setError('Server error'); }
        finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
                <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-6 py-5">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-base font-bold text-white">Reset Password</h2>
                        <button onClick={onClose} className="text-white/70 hover:text-white">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                        </button>
                    </div>
                    <div className="flex gap-1">
                        {['Email', 'OTP', 'Password'].map((_, i) => {
                            const idx = step === 'email' ? 0 : step === 'otp' ? 1 : 2;
                            return <div key={i} className={`flex-1 h-1 rounded-full ${i <= idx ? 'bg-white' : 'bg-white/30'}`}/>;
                        })}
                    </div>
                    <p className="text-white/70 text-xs mt-1.5">
                        Step {step === 'email' ? 1 : step === 'otp' ? 2 : 3} of 3 — {step === 'email' ? 'Enter Email' : step === 'otp' ? 'Verify OTP' : 'Set New Password'}
                    </p>
                </div>

                <div className="p-6">
                    {step === 'email' && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-500">Enter your email to receive a verification code.</p>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                                <input type="email" autoFocus value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
                                    onKeyDown={e => e.key === 'Enter' && handleSendOtp()} placeholder="you@example.com"
                                    className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
                                />
                                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                            </div>
                            <button onClick={handleSendOtp} disabled={loading}
                                className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                                {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>} Send OTP
                            </button>
                        </div>
                    )}

                    {step === 'otp' && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-500 text-center">Code sent to <strong className="text-gray-700">{email}</strong></p>
                            <OtpInput otp={otp} setOtp={setOtp} onEnter={handleVerifyOtp} error={error} />
                            {error && <p className="text-red-500 text-xs text-center">{error}</p>}
                            <div className="text-center text-sm text-gray-500">
                                {!expired ? <>Expires in <Countdown key={timerKey} seconds={60} onExpire={() => setExpired(true)} /></> : <span className="text-red-500 font-medium">OTP expired</span>}
                            </div>
                            <button onClick={handleVerifyOtp} disabled={loading || otp.join('').length !== 6}
                                className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                                {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>} Verify OTP
                            </button>
                            <div className="flex items-center justify-between text-sm">
                                <button onClick={() => { setStep('email'); setError(''); }} className="text-gray-400 hover:text-gray-600">← Back</button>
                                <button onClick={handleResend} disabled={!expired || loading} className="text-teal-600 hover:underline font-semibold disabled:opacity-40 disabled:no-underline">Resend OTP</button>
                            </div>
                        </div>
                    )}

                    {step === 'reset' && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-3 py-2">
                                <svg className="text-green-500 flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                                <p className="text-green-700 text-xs font-semibold">OTP verified — Set your new password</p>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">New Password</label>
                                <input type="password" autoFocus value={newPass} onChange={e => { setNewPass(e.target.value); setError(''); }} placeholder="Minimum 6 characters"
                                    className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm Password</label>
                                <input type="password" value={confirmPass} onChange={e => { setConfirmPass(e.target.value); setError(''); }} onKeyDown={e => e.key === 'Enter' && handleResetPassword()} placeholder="Re-enter new password"
                                    className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
                                />
                                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                            </div>
                            <button onClick={handleResetPassword} disabled={loading}
                                className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                                {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>} Save New Password
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Login Page ────────────────────────────────────────────────────────────
const LoginPage = () => {
    const passwordRef = useRef<HTMLInputElement>(null);
    const [form, setForm] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [forgotOpen, setForgotOpen] = useState(false);

    const handleLogin = async () => {
        if (!form.email || !form.password) { setError('Please fill all fields'); return; }
        setLoading(true); setError('');
        try {
            const res = await fetch(`${SUBS_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: form.email.trim(), password: form.password })
            });
            const data = await res.json();
            if (data.success && data.token) {
                toast.success(`Welcome back! 👋`);
                saveAndRedirect(data.token, data.user);
            } else setError(data.message || 'Invalid email or password');
        } catch { setError('Server error. Try again.'); }
        finally { setLoading(false); }
    };

    const handleGoogleSuccess = async (credentialResponse: any) => {
        try {
            const res = await fetch(`${SUBS_URL}/auth/google`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken: credentialResponse.credential })
            });
            const data = await res.json();
            if (data.success) { toast.success(`Welcome! 👋`); saveAndRedirect(data.token, data.user); }
            else toast.error(data.message || 'Google sign-in failed');
        } catch { toast.error('Google sign-in failed'); }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2 mb-5">
                        <div className="w-9 h-9 bg-teal-600 rounded-xl flex items-center justify-center shadow-sm">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                            </svg>
                        </div>
                        <span className="text-xl font-bold text-gray-800">Med<span className="text-teal-600">Code</span><span className="text-gray-400 font-medium">Lookup</span></span>
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
                    <p className="text-sm text-gray-500 mt-1">Sign in to your account</p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                    <div className="space-y-5">
                        {/* Email */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                            <input type="email" autoFocus value={form.email}
                                onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setError(''); }}
                                onKeyDown={e => { if (e.key === 'Tab') { e.preventDefault(); passwordRef.current?.focus(); } }}
                                placeholder="you@example.com"
                                className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all text-gray-800 placeholder-gray-400 ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
                            />
                        </div>

                        {/* Password + Forgot */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="text-sm font-semibold text-gray-700">Password</label>
                                <button onClick={() => setForgotOpen(true)} className="text-[12px] text-teal-600 hover:text-teal-700 font-semibold hover:underline">
                                    Forgot Password?
                                </button>
                            </div>
                            <input ref={passwordRef} type="password" value={form.password}
                                onChange={e => { setForm(f => ({ ...f, password: e.target.value })); setError(''); }}
                                onKeyDown={e => { if (e.key === 'Enter') handleLogin(); }}
                                placeholder="••••••••"
                                className={`w-full px-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all text-gray-800 ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}
                            />
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                                <svg className="text-red-400 flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                <p className="text-red-600 text-xs font-medium">{error}</p>
                            </div>
                        )}

                        {/* Sign In button */}
                        <button onClick={handleLogin} disabled={loading}
                            className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2 text-sm">
                            {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> Signing in...</> : 'Sign In'}
                        </button>

                        {/* Divider */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1 h-px bg-gray-100"/>
                            <span className="text-xs text-gray-400">or</span>
                            <div className="flex-1 h-px bg-gray-100"/>
                        </div>

                        {/* ✅ Google Button — Sign In ke baad, Create Account se pehle */}
                        <div className="flex justify-center">
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => toast.error('Google sign-in failed')}
                                text="signin_with"
                                shape="rectangular"
                                theme="outline"
                                size="large"
                                width="368"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 my-5">
                        <div className="flex-1 h-px bg-gray-100"/>
                        <span className="text-xs text-gray-400">new here?</span>
                        <div className="flex-1 h-px bg-gray-100"/>
                    </div>

                    {/* Create Account — sab se neche */}
                    <Link to="/register" className="block w-full text-center py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-teal-300 transition-all">
                        Create Free Account
                    </Link>
                </div>
                <p className="text-center text-xs text-gray-400 mt-4">🔒 Secure & encrypted.</p>
            </div>

            {forgotOpen && <ForgotPasswordModal onClose={() => setForgotOpen(false)} />}
        </div>
    );
};

export default LoginPage;